import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { OperatorPageShell, Button, ConfirmDialog } from '@/components';
import { useAuthFetch } from '@/hooks';
import { ROUTES } from '../../routes.ts';
import { Th } from './operatorTableHelpers.tsx';
import panel from './operatorPanel.module.css';
import { REOPENABLE_CLOSED_REASONS } from '@dermalink/shared';
import type {
  DeleteStaleConversationsResponse,
  OperatorDoctorListResponse,
  OperatorDoctorSummary,
  OutstandingPaymentItem,
  OutstandingPaymentsResponse,
  RecoverableConversationItem,
  RecoverableConversationsResponse,
  ReopenConversationRequest,
  ReopenConversationResponse,
  SendConversationReportRequest,
  SendConversationReportResponse,
  StaleConversationItem,
  StaleConversationsResponse,
} from '@dermalink/shared';

function formatEuroFromCents(cents: number): string {
  return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

const REMINDER_STAGE_LABEL = ['Nog geen', 'Vriendelijk', 'Waarschuwing', 'Laatste'];

export function OperatorHygienePanel() {
  const authFetch = useAuthFetch();
  const [data, setData]         = useState<StaleConversationsResponse | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(false);
  const [confirming, setConfirming]   = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const [lastDeleted, setLastDeleted] = useState<number | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    authFetch('/operator/stale-conversations')
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((d: StaleConversationsResponse) => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  async function handleDelete() {
    setDeleting(true);
    try {
      const res = await authFetch('/operator/stale-conversations', { method: 'DELETE' });
      if (!res.ok) throw new Error();
      const result = await res.json() as DeleteStaleConversationsResponse;
      setLastDeleted(result.deleted);
      setConfirming(false);
      load();
    } catch {
      setDeleting(false);
      setConfirming(false);
    }
  }

  const staleCount = data?.conversations.length ?? 0;

  return (
    <div className={panel.section}>
      <h2 className={panel.sectionHeading}>Verlopen consulten</h2>
      <p className={panel.sectionDescription}>
        Consulten die zijn aangemaakt maar waarvoor de betaling nooit is voltooid. Deze zijn onzichtbaar voor patiënten en artsen en kunnen veilig worden verwijderd.
      </p>

      {data && (
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)', flexWrap: 'wrap' }}>
          <StatChip label="Verlopen (verwijderbaar)" value={staleCount} warn={staleCount > 0} />
          <StatChip label="Wachtend op betaling" value={data.pending_count} />
        </div>
      )}

      {loading && <p className="text-muted status-message">Laden…</p>}
      {error   && <p className="text-muted status-message">Kon gegevens niet laden.</p>}

      {lastDeleted !== null && (
        <p className={panel.successText} style={{ marginBottom: 'var(--space-2)' }}>
          {lastDeleted} {lastDeleted === 1 ? 'consult' : 'consulten'} verwijderd.
        </p>
      )}

      {data && !loading && (
        <>
          {staleCount > 0 && (
            <div style={{ marginBottom: 'var(--space-2)' }}>
              <Button variant="danger" onClick={() => { setConfirming(true); setLastDeleted(null); }}>
                Verwijder {staleCount} verlopen {staleCount === 1 ? 'consult' : 'consulten'}
              </Button>
            </div>
          )}

          <div className={panel.tableWrap}>
            <table className={panel.table}>
              <thead>
                <tr>
                  <Th>ID</Th>
                  <Th>Patiënt</Th>
                  <Th>Arts</Th>
                  <Th>Aangemaakt</Th>
                  <Th>Verlopen op</Th>
                </tr>
              </thead>
              <tbody>
                {data.conversations.map((c) => <StaleRow key={c.id} item={c} />)}
                {staleCount === 0 && (
                  <tr><td colSpan={5} className={panel.emptyCell}>Geen verlopen consulten gevonden.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {confirming && (
        <ConfirmDialog
          title="Verlopen consulten verwijderen"
          message={`Wil je ${staleCount} ${staleCount === 1 ? 'verlopen consult' : 'verlopen consulten'} permanent verwijderen? Deze actie kan niet ongedaan worden gemaakt.`}
          confirmLabel="Verwijderen"
          variant="danger"
          confirming={deleting}
          onConfirm={handleDelete}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  );
}

export function OperatorHygiene() {
  return (
    <OperatorPageShell maxWidth="1400px">
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <Link to={ROUTES.operator} className={panel.mutedText}>← Beheerder</Link>
        <h1 className="page-heading" style={{ margin: 0 }}>Gegevensbeheer</h1>
      </div>
      <OperatorHygienePanel />
      <OutstandingPaymentsPanel />
      <RecoverableConversationsPanel />
    </OperatorPageShell>
  );
}

export function OutstandingPaymentsPanel() {
  const authFetch = useAuthFetch();
  const [data, setData]       = useState<OutstandingPaymentsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    authFetch('/operator/payments/outstanding')
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((d: OutstandingPaymentsResponse) => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const items       = data?.conversations ?? [];
  const pendingCount = items.filter((i) => i.payment_status === 'pending').length;
  const paidCount    = items.filter((i) => i.payment_status === 'paid').length;

  return (
    <div className={panel.section}>
      <h2 className={panel.sectionHeading}>Openstaande betalingen</h2>
      <p className={panel.sectionDescription}>
        Gesloten consulten met een betaling die aandacht nodig heeft: nog niet betaald (wordt automatisch herinnerd),
        of alsnog betaald op een consult dat de arts al geforceerd heeft afgesloten — dat bedrag is niet automatisch
        terugbetaald en moet manueel beoordeeld worden.
      </p>

      {data && (
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)', flexWrap: 'wrap' }}>
          <StatChip label="Nog niet betaald" value={pendingCount} warn={pendingCount > 0} />
          <StatChip label="Betaald na geforceerd sluiten" value={paidCount} warn={paidCount > 0} />
        </div>
      )}

      {loading && <p className="text-muted status-message">Laden…</p>}
      {error   && <p className="text-muted status-message">Kon gegevens niet laden.</p>}

      {data && !loading && (
        <div className={panel.tableWrap}>
          <table className={panel.table}>
            <thead>
              <tr>
                <Th>ID</Th>
                <Th>Patiënt</Th>
                <Th>Bedrag</Th>
                <Th>Status</Th>
                <Th>Gesloten op</Th>
                <Th>Reden</Th>
                <Th>Herinnering</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => <OutstandingPaymentRow key={item.id} item={item} />)}
              {items.length === 0 && (
                <tr><td colSpan={7} className={panel.emptyCell}>Geen openstaande betalingen.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OutstandingPaymentRow({ item }: { item: OutstandingPaymentItem }) {
  const fmt = (s: string) => new Date(s).toLocaleDateString('nl-BE');
  const patientName = [item.patient_first_name, item.patient_last_name].filter(Boolean).join(' ') || '—';
  const needsRefundReview = item.payment_status === 'paid';
  return (
    <tr>
      <td className={panel.td} style={{ fontFamily: 'monospace', color: 'var(--c-text-muted)' }}>{item.id}</td>
      <td className={panel.td}>{patientName}</td>
      <td className={panel.td}>{formatEuroFromCents(item.amount_cents)}</td>
      <td className={panel.td}>
        <span className={`${panel.pill} ${needsRefundReview ? panel.pillDanger : panel.pillWarning}`}>
          {needsRefundReview ? 'Betaald — controleer terugbetaling' : 'Wachtend op betaling'}
        </span>
      </td>
      <td className={panel.td} style={{ whiteSpace: 'nowrap' }}>{fmt(item.closed_at)}</td>
      <td className={panel.td} style={{ color: 'var(--c-text-muted)' }}>{item.closed_reason ?? '—'}</td>
      <td className={panel.td} style={{ color: 'var(--c-text-muted)' }}>
        {needsRefundReview ? '—' : REMINDER_STAGE_LABEL[item.reminder_stage] ?? item.reminder_stage}
      </td>
    </tr>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className={panel.fieldLabel}>
      {label}
      {children}
    </label>
  );
}

export function RecoverableConversationsPanel() {
  const authFetch = useAuthFetch();
  const [doctors, setDoctors]           = useState<OperatorDoctorSummary[]>([]);
  const [doctorSub, setDoctorSub]       = useState('');
  const [initials, setInitials]         = useState('');
  const [closedAfter, setClosedAfter]   = useState('');
  const [closedBefore, setClosedBefore] = useState('');
  const [results, setResults]           = useState<RecoverableConversationItem[] | null>(null);
  const [searching, setSearching]       = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [notice, setNotice]             = useState<string | null>(null);
  const [restoringId, setRestoringId]   = useState<number | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<RecoverableConversationItem | null>(null);

  useEffect(() => {
    authFetch('/operator/doctors')
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((d: OperatorDoctorListResponse) => setDoctors(d.doctors))
      .catch(() => {});
  }, [authFetch]);

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setSearching(true);
    setError(null);
    setNotice(null);
    setResults(null);
    try {
      const params = new URLSearchParams({ initials, closed_after: closedAfter, closed_before: closedBefore });
      const res = await authFetch(`/operator/doctors/${doctorSub}/deleted-conversations?${params}`);
      if (!res.ok) throw new Error();
      const data: RecoverableConversationsResponse = await res.json();
      setResults(data.conversations);
    } catch {
      setError('Zoeken mislukt.');
    } finally {
      setSearching(false);
    }
  }

  async function handleSendReport() {
    if (!confirmTarget) return;
    setRestoringId(confirmTarget.id);
    try {
      const reqBody: SendConversationReportRequest = { doctor_sub: doctorSub };
      const res = await authFetch(`/operator/conversations/${confirmTarget.id}/send-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reqBody),
      });
      if (!res.ok) throw new Error();
      await res.json() as SendConversationReportResponse;
      setResults((prev) => prev?.filter((r) => r.id !== confirmTarget.id) ?? null);
      setNotice(`Rapport van consult #${confirmTarget.id} is naar de arts gemaild. Het consult blijft verborgen.`);
      setConfirmTarget(null);
    } catch {
      setError('Versturen van het rapport is mislukt.');
    } finally {
      setRestoringId(null);
    }
  }

  async function handleReopen() {
    if (!confirmTarget) return;
    setRestoringId(confirmTarget.id);
    try {
      const reqBody: ReopenConversationRequest = { doctor_sub: doctorSub };
      const res = await authFetch(`/operator/conversations/${confirmTarget.id}/reopen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reqBody),
      });
      if (!res.ok) throw new Error();
      await res.json() as ReopenConversationResponse;
      setResults((prev) => prev?.filter((r) => r.id !== confirmTarget.id) ?? null);
      setNotice(`Consult #${confirmTarget.id} heropend — de patiënt is op de hoogte gebracht.`);
      setConfirmTarget(null);
    } catch {
      setError('Heropenen mislukt.');
    } finally {
      setRestoringId(null);
    }
  }

  const doctorName = (d: OperatorDoctorSummary) => [d.first_name, d.last_name].filter(Boolean).join(' ') || d.email || d.cognito_sub;
  const canSearch = doctorSub && initials.trim().length >= 2 && closedAfter && closedBefore;

  return (
    <div className={panel.section}>
      <h2 className={panel.sectionHeading}>Verwijderde consulten herstellen</h2>
      <p className={panel.sectionDescription}>
        Gesloten consulten worden 2 weken na sluiting automatisch verborgen. Zoek enkel op uitdrukkelijk verzoek van de betrokken arts — er is geen volledige lijst, initialen en een sluitingsperiode zijn verplicht.
      </p>

      <form onSubmit={handleSearch} className={panel.toolbarEnd} style={{ marginBottom: 'var(--space-2)' }}>
        <Field label="Arts">
          <select value={doctorSub} onChange={(e) => setDoctorSub(e.target.value)} required className={panel.field} style={{ minWidth: '200px' }}>
            <option value="">Kies een arts…</option>
            {doctors.map((d) => <option key={d.cognito_sub} value={d.cognito_sub}>{doctorName(d)}</option>)}
          </select>
        </Field>
        <Field label="Initialen patiënt">
          <input value={initials} onChange={(e) => setInitials(e.target.value)} placeholder="J.D." maxLength={5} required className={panel.field} style={{ width: '90px' }} />
        </Field>
        <Field label="Gesloten na">
          <input type="date" value={closedAfter} onChange={(e) => setClosedAfter(e.target.value)} required className={panel.field} />
        </Field>
        <Field label="Gesloten voor">
          <input type="date" value={closedBefore} onChange={(e) => setClosedBefore(e.target.value)} required className={panel.field} />
        </Field>
        <Button type="submit" disabled={!canSearch || searching}>{searching ? 'Zoeken…' : 'Zoeken'}</Button>
      </form>

      {error  && <p className={panel.errorText} style={{ marginBottom: 'var(--space-2)' }}>{error}</p>}
      {notice && <p className={panel.successText} style={{ marginBottom: 'var(--space-2)' }}>{notice}</p>}

      {results && (
        <div className={panel.tableWrap}>
          <table className={panel.table}>
            <thead>
              <tr>
                <Th>ID</Th>
                <Th>Initialen</Th>
                <Th>Gesloten op</Th>
                <Th>Reden</Th>
                <Th>Betaald</Th>
                <Th>PDF verstuurd</Th>
                <Th>Definitief verwijderd op</Th>
                <Th>{' '}</Th>
              </tr>
            </thead>
            <tbody>
              {results.map((item) => (
                <RecoverableRow key={item.id} item={item} busy={restoringId === item.id} onRestore={() => setConfirmTarget(item)} />
              ))}
              {results.length === 0 && (
                <tr><td colSpan={8} className={panel.emptyCell}>Geen resultaten voor deze zoekopdracht.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {confirmTarget && (() => {
        const reopenable = !confirmTarget.has_paid_payment
          && (REOPENABLE_CLOSED_REASONS as readonly (string | null)[]).includes(confirmTarget.closed_reason);
        return (
          <ConfirmDialog
            title="Verwijderd consult"
            message={
              reopenable
                ? `Consult #${confirmTarget.id} blijft verborgen. Kies "Rapport naar arts sturen" om enkel een PDF-kopie te e-mailen naar de arts (geen impact op de verwijdertermijn), of "Heropenen" om het gesprek weer actief te maken — de patiënt ontvangt dan een e-mail en kan opnieuw reageren.`
                : `Consult #${confirmTarget.id} blijft verborgen voor patiënt en arts — enkel een PDF-kopie van het volledige gesprek wordt naar de arts gemaild. De verwijdertermijn loopt gewoon door.`
            }
            confirmLabel="Rapport naar arts sturen"
            secondaryConfirmLabel={reopenable ? 'Heropenen' : undefined}
            onSecondaryConfirm={reopenable ? handleReopen : undefined}
            confirming={restoringId === confirmTarget.id}
            onConfirm={handleSendReport}
            onCancel={() => setConfirmTarget(null)}
          />
        );
      })()}
    </div>
  );
}

function RecoverableRow({ item, busy, onRestore }: { item: RecoverableConversationItem; busy: boolean; onRestore: () => void }) {
  const fmt = (s: string) => new Date(s).toLocaleDateString('nl-BE');
  return (
    <tr>
      <td className={panel.td} style={{ fontFamily: 'monospace', color: 'var(--c-text-muted)' }}>{item.id}</td>
      <td className={panel.td}>{item.patient_initials}</td>
      <td className={panel.td} style={{ whiteSpace: 'nowrap' }}>{fmt(item.closed_at)}</td>
      <td className={panel.td} style={{ color: 'var(--c-text-muted)' }}>{item.closed_reason ?? '—'}</td>
      <td className={panel.td}>{item.has_paid_payment ? 'Ja' : 'Nee'}</td>
      <td className={panel.td}>{item.pdf_emailed ? 'Ja' : 'Nee'}</td>
      <td className={panel.td} style={{ color: 'var(--c-warning-text)', whiteSpace: 'nowrap' }}>{fmt(item.scrub_at)}</td>
      <td className={panel.td}>
        <Button variant="ghost" onClick={onRestore} disabled={busy}>Herstellen…</Button>
      </td>
    </tr>
  );
}

function StaleRow({ item: c }: { item: StaleConversationItem }) {
  const fmt = (s: string) => new Date(s).toLocaleDateString('nl-BE');
  const patientName = [c.patient_first_name, c.patient_last_name].filter(Boolean).join(' ') || '—';
  const doctorName  = [c.doctor_first_name,  c.doctor_last_name ].filter(Boolean).join(' ') || '—';
  return (
    <tr>
      <td className={panel.td} style={{ fontFamily: 'monospace', color: 'var(--c-text-muted)' }}>{c.id}</td>
      <td className={panel.td}>{patientName}</td>
      <td className={panel.td}>{doctorName}</td>
      <td className={panel.td} style={{ color: 'var(--c-text-muted)', whiteSpace: 'nowrap' }}>{fmt(c.created_at)}</td>
      <td className={panel.td} style={{ color: 'var(--c-warning-text)', whiteSpace: 'nowrap' }}>{fmt(c.expires_at)}</td>
    </tr>
  );
}

function StatChip({ label, value, warn = false }: { label: string; value: number; warn?: boolean }) {
  return (
    <div className={`${panel.statChip}${warn ? ` ${panel.statChipWarn}` : ''}`}>
      <div className={panel.statChipValue}>{value}</div>
      <div className={panel.statChipLabel}>{label}</div>
    </div>
  );
}
